import { useCallback, useEffect, useMemo, useState } from "react";
import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Loader2, Trash2, Upload, FileText, Sparkles, RefreshCcw, LogOut } from "lucide-react";
import { testApi, setTestToken, getTestToken, recordEvent } from "@/lib/testApi";

/**
 * Real-document opportunity flow — /app/demo/opportunities/:id.
 * Genuine (non-mocked) upload → extract → estimate → GPT-5.4 analysis →
 * user-editable claim review → Decision Passport with grounded citations.
 */

const CATEGORY_LABEL = {
  source_supported_fact: "Source-supported fact",
  ai_inference: "AI-generated interpretation",
  unresolved_question: "Unresolved question",
  contradictory_evidence: "Contradictory evidence",
  specialist_review_required: "Requires specialist review",
};
const CATEGORY_TONE = {
  source_supported_fact: "verified",
  ai_inference: "supported",
  unresolved_question: "review",
  contradictory_evidence: "contradiction",
  specialist_review_required: "review",
};

export default function RealOpportunity() {
  const { id } = useParams();
  const isNew = id === "new";
  const navigate = useNavigate();

  const [opp, setOpp] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [claims, setClaims] = useState([]);
  const [passport, setPassport] = useState(null);
  const [contradictionRegister, setContradictionRegister] = useState(null);
  const [releaseCheck, setReleaseCheck] = useState(null);
  const [versions, setVersions] = useState([]);
  const [diff, setDiff] = useState(null);
  const [limits, setLimits] = useState(null);
  const [busy, setBusy] = useState(false);
  const [analysing, setAnalysing] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [citationDoc, setCitationDoc] = useState(null);

  // Create-opportunity form
  const [form, setForm] = useState({ name: "", thesis: "", proposed_amount: "", stage: "", decision_deadline: "" });

  useEffect(() => { document.title = "Live Opportunity · ImpactVest Test Workspace"; }, []);

  useEffect(() => {
    if (!getTestToken()) { navigate("/access", { replace: true }); return; }
    testApi.get("/test/limits").then((r) => setLimits(r.data.limits)).catch(() => {});
  }, [navigate]);

  const load = useCallback(async () => {
    if (isNew) return;
    try {
      const [o, e, c] = await Promise.all([
        testApi.get(`/test/opportunities/${id}`),
        testApi.get(`/test/opportunities/${id}/estimate`),
        testApi.get(`/test/opportunities/${id}/claims`),
      ]);
      setOpp(o.data.opportunity); setEstimate(e.data); setClaims(c.data.claims || []);
      if (o.data.opportunity.analysis_status === "completed") {
        const [p, cr, rc, vs] = await Promise.all([
          testApi.get(`/test/opportunities/${id}/passport`),
          testApi.get(`/test/opportunities/${id}/contradiction-register`),
          testApi.get(`/test/opportunities/${id}/passport/release-check`),
          testApi.get(`/test/opportunities/${id}/passport/versions`),
        ]);
        setPassport(p.data);
        setContradictionRegister(cr.data);
        setReleaseCheck(rc.data);
        setVersions(vs.data.versions || []);
      }
    } catch (err) {
      if (err?.response?.status === 401) { setTestToken(null); navigate("/access", { replace: true }); return; }
      if (err?.response?.status === 404) { toast.error("Opportunity not found."); navigate("/app/demo", { replace: true }); return; }
      toast.error("Unable to load opportunity.");
    }
  }, [id, isNew, navigate]);

  useEffect(() => { load(); }, [load]);

  const createOpp = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) { toast.error("Please give the opportunity a name."); return; }
    setBusy(true);
    try {
      const r = await testApi.post("/test/opportunities", form);
      recordEvent("live_opportunity_application_started");
      navigate(`/app/demo/opportunities/${r.data.opportunity.id}`, { replace: true });
    } catch { toast.error("Unable to create opportunity."); }
    finally { setBusy(false); }
  };

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    for (const f of Array.from(files)) {
      const fd = new FormData(); fd.append("file", f);
      try {
        const r = await testApi.post(`/test/opportunities/${id}/documents`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (r.data.duplicate) toast.info(`${f.name} is a duplicate — kept the existing copy.`);
        else toast.success(`${f.name} uploaded.`);
      } catch (err) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        if (status === 415) toast.error(`${f.name}: unsupported file type.`);
        else if (status === 413) toast.error(`${f.name}: ${detail || "too large."}`);
        else if (status === 422) toast.error(`${f.name}: ${detail || "unreadable — please retry with a different copy."}`);
        else toast.error(`${f.name}: upload failed.`);
      }
    }
    await load();
  };

  const removeDoc = async (docId) => {
    if (!window.confirm("Remove this document and its extracted content?")) return;
    try { await testApi.delete(`/test/opportunities/${id}/documents/${docId}`); toast.success("Removed."); await load(); }
    catch { toast.error("Unable to remove."); }
  };

  const runAnalyze = async () => {
    setAnalysing(true);
    try {
      const r = await testApi.post(`/test/opportunities/${id}/analyze`);
      toast.success(`Analysis complete — ${r.data.claims} claims extracted.`);
      recordEvent("decision_passport_generated");
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 402) toast.error(`Over limits: ${(detail?.exceeds || []).join(" ")}`);
      else if (typeof detail === "string") toast.error(detail);
      else toast.error("Analysis failed. Please retry.");
    } finally { setAnalysing(false); }
  };

  const patchClaim = async (claim, updates) => {
    try {
      const r = await testApi.patch(`/test/opportunities/${id}/claims/${claim.id}`, updates);
      setClaims((cs) => cs.map((c) => c.id === claim.id ? r.data.claim : c));
      if (passport) {
        // Refresh every downstream surface that a claim mutation can affect:
        // passport payload, contradiction register (resolution / assignment),
        // release-check (blocker recount → gate status), and version list
        // (an upcoming release note or diff base).
        const [p, cr, rc, vs] = await Promise.all([
          testApi.get(`/test/opportunities/${id}/passport`),
          testApi.get(`/test/opportunities/${id}/contradiction-register`),
          testApi.get(`/test/opportunities/${id}/passport/release-check`),
          testApi.get(`/test/opportunities/${id}/passport/versions`),
        ]);
        setPassport(p.data);
        setContradictionRegister(cr.data);
        setReleaseCheck(rc.data);
        setVersions(vs.data.versions || []);
      }
    } catch { toast.error("Unable to update claim."); }
  };

  const openCitation = async (docId) => {
    try {
      const doc = opp.documents.find((d) => d.id === docId);
      const chunks = await testApi.get(`/test/opportunities/${id}/documents/${docId}/chunks`);
      setCitationDoc({ doc, chunks: chunks.data.extract.chunks });
      recordEvent("source_citation_opened", { document_id: docId });
    } catch { toast.error("Unable to open source."); }
  };

  // -------- Render ----------
  if (isNew) return (
    <Shell>
      <form onSubmit={createOpp} className="iv-canvas rounded-[3px] p-8 max-w-2xl mx-auto" data-testid="opp-create-form">
        <div className="iv-canvas-eyebrow mb-4">Create a live opportunity</div>
        <h2 className="font-serif-display text-[26px] text-[var(--ivc-ivory)] leading-tight">Bring your own materials to review.</h2>
        <p className="text-[13.5px] text-[var(--ivc-ivory-2)] mt-2">Upload PDF, DOCX, XLSX and CSV documents. ImpactVest extracts the content and — with your review — turns it into a Decision Passport. Fictional demonstration only in the test workspace.</p>
        <label className="iv-field mt-6"><span>Opportunity name</span>
          <input required data-testid="opp-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Aurelia Bio Renewables Series C" /></label>
        <label className="iv-field mt-4"><span>Proposed thesis</span>
          <textarea rows={3} data-testid="opp-thesis" value={form.thesis} onChange={(e) => setForm({ ...form, thesis: e.target.value })} placeholder="What is the reason to invest?" /></label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <label className="iv-field"><span>Proposed amount</span>
            <input data-testid="opp-amount" value={form.proposed_amount} onChange={(e) => setForm({ ...form, proposed_amount: e.target.value })} placeholder="€10M" /></label>
          <label className="iv-field"><span>Stage</span>
            <input value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })} placeholder="Series C" /></label>
          <label className="iv-field"><span>Decision deadline</span>
            <input type="date" value={form.decision_deadline} onChange={(e) => setForm({ ...form, decision_deadline: e.target.value })} /></label>
        </div>
        <div className="mt-6 flex items-center gap-3">
          <button type="submit" disabled={busy} className="iv-btn iv-btn--primary" data-testid="opp-create-submit">
            {busy ? "Creating…" : <>Create opportunity <ArrowRight size={14} /></>}
          </button>
          <Link to="/app/demo" className="iv-btn iv-btn--ghost">Back</Link>
        </div>
      </form>
    </Shell>
  );

  if (!opp) return <Shell><Loading /></Shell>;

  const isCompleted = opp.analysis_status === "completed" && passport;

  return (
    <Shell>
      <div className="space-y-6" data-testid="opp-workspace">
        {/* Header */}
        <div className="iv-canvas rounded-[3px] px-6 md:px-10 py-6">
          <div className="iv-canvas-eyebrow mb-3">Live opportunity · {opp.analysis_status || "draft"}</div>
          <h2 className="font-serif-display text-[26px] md:text-[32px] leading-tight text-[var(--ivc-ivory)]">{opp.name}</h2>
          {opp.thesis && <p className="font-serif-display italic text-[16px] leading-relaxed text-[var(--ivc-ivory-2)] mt-3 max-w-3xl">&ldquo;{opp.thesis}&rdquo;</p>}
          <div className="mt-4 flex flex-wrap gap-x-8 gap-y-1 font-mono text-[10.5px] tracking-[0.14em] uppercase text-[var(--ivc-ivory-3)]">
            {opp.proposed_amount && <span>Amount · {opp.proposed_amount}</span>}
            {opp.stage && <span>Stage · {opp.stage}</span>}
            {opp.decision_deadline && <span>Deadline · {opp.decision_deadline}</span>}
          </div>
        </div>

        {/* Upload zone */}
        <div className="iv-canvas rounded-[3px] px-6 md:px-10 py-6" data-testid="opp-upload">
          <div className="flex items-center justify-between mb-3">
            <div className="iv-canvas-eyebrow">Diligence documents</div>
            {limits && <span className="font-mono text-[10px] tracking-[0.14em] uppercase text-[var(--ivc-ivory-3)]">Up to {limits.max_docs_per_opportunity} docs · {limits.max_file_mb} MB each · PDF/DOCX/XLSX/CSV</span>}
          </div>
          <label className="border border-dashed border-[var(--ivc-line-warm)] rounded-[3px] p-6 flex flex-col items-center justify-center text-center cursor-pointer hover:border-[var(--ivc-gold-2)]">
            <input type="file" multiple accept=".pdf,.docx,.xlsx,.csv" className="hidden" data-testid="opp-file-input"
              onChange={(e) => upload(e.target.files)} />
            <Upload size={20} className="text-[var(--ivc-gold-2)]" strokeWidth={1.4} />
            <span className="mt-2 text-[13.5px] text-[var(--ivc-ivory)]">Click to add PDF, DOCX, XLSX or CSV files</span>
            <span className="font-mono text-[10.5px] tracking-[0.12em] uppercase text-[var(--ivc-ivory-3)] mt-1">
              Stored securely inside your test workspace
            </span>
          </label>

          {opp.documents.length > 0 && (
            <ul className="mt-4 divide-y divide-[var(--ivc-line)]">
              {opp.documents.map((d) => (
                <li key={d.id} className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText size={14} className="text-[var(--ivc-gold-2)]" />
                    <div className="min-w-0">
                      <div className="text-[13.5px] text-[var(--ivc-ivory)] truncate">{d.name}</div>
                      <div className="font-mono text-[10.5px] tracking-[0.10em] uppercase text-[var(--ivc-ivory-3)]">
                        {d.kind} · {(d.size_bytes / 1024).toFixed(1)} KB · {d.total_chars} chars · ~{d.estimated_input_tokens} tokens
                      </div>
                    </div>
                  </div>
                  <button onClick={() => removeDoc(d.id)} className="iv-btn iv-btn--ghost" data-testid={`opp-remove-${d.id}`}>
                    <Trash2 size={13} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Estimate + Analyze */}
        {estimate && opp.documents.length > 0 && (
          <div className="iv-canvas rounded-[3px] px-6 md:px-10 py-6" data-testid="opp-estimate">
            <div className="iv-canvas-eyebrow mb-3">Pre-analysis estimate</div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-[13.5px] text-[var(--ivc-ivory)]">
              <Stat label="Documents" value={estimate.documents} />
              <Stat label="Extracted chars" value={estimate.total_chars.toLocaleString()} />
              <Stat label="Input tokens (est.)" value={estimate.estimated_input_tokens.toLocaleString()} />
              <Stat label="Cost (est.)" value={`$${estimate.estimated_cost_usd.toFixed(4)}`} />
              <Stat label="Model" value={estimate.model} />
            </div>
            {estimate.exceeds?.length > 0 && (
              <div className="mt-4 border-l-2 border-[var(--ivc-amber)] pl-4">
                <div className="font-mono text-[10.5px] tracking-[0.14em] uppercase text-[var(--ivc-amber)]">Over limits</div>
                <ul className="text-[13px] text-[var(--ivc-ivory)] mt-1">
                  {estimate.exceeds.map((e, i) => <li key={i}>• {e}</li>)}
                </ul>
              </div>
            )}
            <div className="mt-5 flex items-center gap-3">
              <button onClick={runAnalyze} disabled={analysing || !estimate.ready_to_analyse}
                className="iv-btn iv-btn--primary" data-testid="opp-analyze">
                {analysing ? <><Loader2 size={13} className="animate-spin" /> Analysing…</> : <><Sparkles size={13} /> Run analysis</>}
              </button>
              <span className="font-mono text-[10.5px] tracking-[0.12em] uppercase text-[var(--ivc-ivory-3)]">
                No confidential documents · Test workspace · Fictional demonstration
              </span>
            </div>
          </div>
        )}

        {/* Claims review */}
        {claims.length > 0 && (
          <div className="iv-canvas rounded-[3px] px-6 md:px-10 py-6" data-testid="opp-claims">
            <div className="iv-canvas-eyebrow mb-3">Extracted claims · Review before Passport</div>
            <p className="text-[13px] text-[var(--ivc-ivory-2)] max-w-3xl mb-4">
              Every claim carries a category and preserved citations. Correct, annotate or remove any claim
              before it enters the Decision Passport.
            </p>
            <ul className="divide-y divide-[var(--ivc-line)]">
              {claims.map((c) => (
                <li key={c.id} className={`py-4 grid grid-cols-[auto_1fr] gap-4 ${c.excluded ? "opacity-40" : ""}`} data-testid={`claim-${c.id}`}>
                  <span className={`iv-ev-dot iv-ev-dot--${CATEGORY_TONE[c.category]} mt-1.5`} />
                  <div>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <p className="text-[14.5px] text-[var(--ivc-ivory)] leading-snug flex-1 min-w-0">{c.label}</p>
                      {c.evidence_strength && (
                        <span className={`iv-strength-badge iv-strength-badge--${c.evidence_strength.tier}`}
                              title={c.evidence_strength.rationale?.join(" · ") || ""}
                              data-testid={`claim-${c.id}-strength`}>
                          Evidence · {c.evidence_strength.tier} · {c.evidence_strength.score}
                        </span>
                      )}
                    </div>
                    {c.evidence_strength?.rationale?.length > 0 && (
                      <ul className="mt-1 space-y-0.5 font-mono text-[10.5px] tracking-[0.02em] text-[var(--ivc-ivory-3)]" data-testid={`claim-${c.id}-strength-rationale`}>
                        {c.evidence_strength.rationale.slice(0, 3).map((r, i) => (
                          <li key={i}>· {r}</li>
                        ))}
                      </ul>
                    )}
                    <div className="mt-1 flex flex-wrap items-center gap-3 font-mono text-[10.5px] tracking-[0.12em] uppercase text-[var(--ivc-ivory-3)]">
                      <select value={c.category} onChange={(e) => patchClaim(c, { category: e.target.value })}
                        className="bg-transparent border border-[var(--ivc-line-strong)] px-2 py-1 text-[10.5px] uppercase" data-testid={`claim-${c.id}-category`}>
                        <option value="source_supported_fact">Source-supported</option>
                        <option value="ai_inference">AI interpretation</option>
                        <option value="unresolved_question">Unresolved question</option>
                        <option value="contradictory_evidence">Contradictory evidence</option>
                        <option value="specialist_review_required">Specialist review</option>
                      </select>
                      <span>Materiality · {c.materiality}</span>
                    </div>
                    {c.citations.length > 0 && (
                      <ul className="mt-3 space-y-1.5">
                        {c.citations.map((ct, i) => {
                          const doc = opp.documents.find((d) => d.id === ct.document_id);
                          return (
                            <li key={i} className="border-l border-[var(--ivc-line-warm)] pl-3">
                              <button onClick={() => openCitation(ct.document_id)}
                                className="text-left font-mono text-[10.5px] tracking-[0.06em] text-[var(--ivc-gold-2)] hover:underline">
                                {doc?.name || "Document"} · {ct.location}
                              </button>
                              {ct.excerpt && <p className="font-serif-display italic text-[13px] leading-relaxed text-[var(--ivc-ivory-2)] mt-1">&ldquo;{ct.excerpt}&rdquo;</p>}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                    <input placeholder="Add a reviewer annotation…" value={c.user_annotation || ""} onChange={(e) => patchClaim(c, { user_annotation: e.target.value })}
                      className="mt-3 w-full bg-transparent border-b border-[var(--ivc-line-strong)] py-1 text-[12.5px] text-[var(--ivc-ivory-2)]"
                      data-testid={`claim-${c.id}-annotation`} />
                    <div className="mt-2 flex items-center gap-3">
                      <label className="font-mono text-[10.5px] tracking-[0.14em] uppercase text-[var(--ivc-ivory-3)] flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" className="iv-checkbox" checked={!!c.excluded}
                          onChange={(e) => patchClaim(c, { excluded: e.target.checked })}
                          data-testid={`claim-${c.id}-exclude`} />
                        Exclude from Passport
                      </label>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Contradiction Register */}
        {isCompleted && contradictionRegister && contradictionRegister.entries.length > 0 && (
          <div className="iv-canvas rounded-[3px] px-6 md:px-10 py-6" data-testid="contradiction-register">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
              <div className="iv-canvas-eyebrow">Contradiction Register</div>
              <div className="font-mono text-[10.5px] tracking-[0.14em] uppercase text-[var(--ivc-ivory-3)]"
                   data-testid="contradiction-register-summary">
                {contradictionRegister.summary.total} total ·
                <span className="text-[var(--ivc-danger,#B4462B)] ml-1">{contradictionRegister.summary.open} open</span> ·
                <span className="text-[var(--ivc-ivory-2)] ml-1">{contradictionRegister.summary.reviewed} reviewed</span> ·
                <span className="text-[var(--ivc-gold-2)] ml-1">{contradictionRegister.summary.resolved} resolved</span>
              </div>
            </div>
            <p className="text-[13px] text-[var(--ivc-ivory-2)] max-w-3xl mb-4">
              Every point where two source excerpts disagree — presented side-by-side with links to
              the original documents. Record how you resolved each contradiction before the Decision
              Passport is finalised.
            </p>
            <ul className="space-y-5">
              {contradictionRegister.entries.map((e) => (
                <li key={e.id} className="border border-[var(--ivc-line-strong)] rounded-[3px] p-4"
                    data-testid={`contradiction-${e.id}`}>
                  <div className="flex items-start gap-3 flex-wrap">
                    <span className="iv-ev-dot iv-ev-dot--contradiction mt-1.5" />
                    <p className="text-[14.5px] text-[var(--ivc-ivory)] leading-snug flex-1 min-w-0">{e.label}</p>
                    <span className="font-mono text-[10px] tracking-[0.14em] uppercase text-[var(--ivc-ivory-3)]">
                      {e.materiality}{e.jurisdiction ? ` · ${e.jurisdiction}` : ""}
                    </span>
                  </div>
                  <div className="mt-4 grid md:grid-cols-2 gap-4">
                    <ContradictionSide side={e.left} label="Side A" onOpen={openCitation} />
                    <ContradictionSide side={e.right} label="Side B" onOpen={openCitation} />
                  </div>
                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    <label className="font-mono text-[10.5px] tracking-[0.14em] uppercase text-[var(--ivc-ivory-3)]">
                      Resolution
                    </label>
                    <select value={e.resolution?.status || "open"}
                            onChange={(ev) => patchClaim({id: e.id}, { resolution_status: ev.target.value })}
                            className="bg-transparent border border-[var(--ivc-line-strong)] px-2 py-1 text-[10.5px] uppercase text-[var(--ivc-ivory)]"
                            data-testid={`contradiction-${e.id}-status`}>
                      <option value="open">Open</option>
                      <option value="reviewed">Reviewed</option>
                      <option value="resolved">Resolved</option>
                    </select>
                    <input placeholder="How was this contradiction resolved?"
                           defaultValue={e.resolution?.note || ""}
                           onBlur={(ev) => {
                             const v = ev.target.value;
                             if (v !== (e.resolution?.note || "")) patchClaim({id: e.id}, { resolution_note: v });
                           }}
                           className="flex-1 min-w-[200px] bg-transparent border-b border-[var(--ivc-line-strong)] py-1 text-[12.5px] text-[var(--ivc-ivory-2)]"
                           data-testid={`contradiction-${e.id}-note`} />
                  </div>
                  {/*
                    Reviewer assignment — opaque handle only. The label the
                    user types is the ONLY thing the Register / Passport ever
                    displays. ImpactVest never auto-populates a name here and
                    never implies human review when no assignment is recorded.
                  */}
                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <label className="font-mono text-[10.5px] tracking-[0.14em] uppercase text-[var(--ivc-ivory-3)]">
                      Assigned to
                    </label>
                    <input placeholder="Optional reviewer handle (e.g. Analyst-1, Legal-EU)"
                           maxLength={120}
                           defaultValue={e.assignment?.assigned_to || ""}
                           onBlur={(ev) => {
                             const v = ev.target.value;
                             const prev = e.assignment?.assigned_to || "";
                             if (v !== prev) patchClaim({id: e.id}, { assigned_to: v });
                           }}
                           className="flex-1 min-w-[220px] bg-transparent border-b border-[var(--ivc-line-strong)] py-1 text-[12.5px] text-[var(--ivc-ivory-2)]"
                           data-testid={`contradiction-${e.id}-assigned-to`} />
                    {e.assignment?.assigned_to ? (
                      <span className="font-mono text-[10px] tracking-[0.14em] uppercase text-[var(--ivc-gold-2)]"
                            data-testid={`contradiction-${e.id}-assignment-label`}>
                        Recorded {new Date(e.assignment.assigned_at).toISOString().slice(0,10)}
                      </span>
                    ) : (
                      <span className="font-mono text-[10px] tracking-[0.14em] uppercase text-[var(--ivc-ivory-3)]"
                            data-testid={`contradiction-${e.id}-assignment-none`}>
                        Assignment not recorded
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Release Gate + Passport Versions */}
        {isCompleted && releaseCheck && (
          <ReleaseGatePanel
            releaseCheck={releaseCheck}
            versions={versions}
            diff={diff}
            releasing={releasing}
            onRelease={async (note) => {
              setReleasing(true);
              try {
                const r = await testApi.post(
                  `/test/opportunities/${id}/passport/versions`,
                  { note: note || "" },
                );
                toast.success(`Passport v${r.data.version.version_num} released.`);
                await load();
              } catch (err) {
                if (err?.response?.status === 409) {
                  toast.error("Release blocked — resolve the gate blockers first.");
                  setReleaseCheck({
                    ...(releaseCheck || {}),
                    ready: false,
                    blockers: err.response.data?.detail?.blockers || [],
                    blocker_codes: (err.response.data?.detail?.blockers || []).map((b) => b.code),
                  });
                } else {
                  toast.error("Unable to release the Passport.");
                }
              } finally { setReleasing(false); }
            }}
            onDiff={async (toId, againstId) => {
              try {
                const url = `/test/opportunities/${id}/passport/versions/${toId}/diff`
                  + (againstId ? `?against=${encodeURIComponent(againstId)}` : "");
                const r = await testApi.get(url);
                setDiff(r.data);
              } catch { toast.error("Unable to load version diff."); }
            }}
            onCloseDiff={() => setDiff(null)}
          />
        )}

        {/* Passport */}
        {isCompleted && (
          <div className="iv-canvas rounded-[3px] px-6 md:px-10 py-8" data-testid="opp-passport">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
              <div className="iv-canvas-eyebrow">Decision Passport · Draft (real analysis)</div>
              <button
                onClick={async () => {
                  try {
                    const r = await testApi.get(`/test/opportunities/${id}/passport.pdf`, { responseType: "blob" });
                    const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
                    const a = document.createElement("a");
                    a.href = url; a.download = `decision-passport-${id.slice(0,8)}.pdf`;
                    document.body.appendChild(a); a.click(); a.remove();
                    URL.revokeObjectURL(url);
                    recordEvent("decision_passport_generated", { format: "pdf" });
                  } catch { toast.error("Unable to download the Passport."); }
                }}
                className="iv-btn iv-btn--ghost" data-testid="opp-passport-pdf">
                Download one-page PDF
              </button>
            </div>
            {passport.evidence_signature && (
              <div className="mb-3 font-mono text-[10.5px] tracking-[0.08em] text-[var(--ivc-ivory-3)]" data-testid="opp-passport-signature">
                Evidence signature · {passport.evidence_signature.slice(0, 20)}…{passport.evidence_signature.slice(-8)}
              </div>
            )}
            <h3 className="font-serif-display text-[24px] text-[var(--ivc-ivory)]">{passport.opportunity.name}</h3>
            <PassportCol title="Original thesis" items={passport.opportunity.thesis ? [passport.opportunity.thesis] : []} />
            <PassportCategory title="Source-supported facts" tone="verified" items={passport.source_supported_facts} openCitation={openCitation} />
            <PassportCategory title="AI-generated interpretation" tone="supported" items={passport.ai_inferences} openCitation={openCitation} />
            <PassportCategory title="Contradictory evidence" tone="contradiction" items={passport.contradictory_evidences} openCitation={openCitation} />
            <PassportCategory title="Unresolved questions" tone="review" items={passport.unresolved_questions} openCitation={openCitation} />
            <PassportCategory title="Requires specialist review" tone="review" items={passport.specialist_review_required} openCitation={openCitation} />
            <TransitionGapMap gaps={passport.transition_gaps} />
            {passport.contradictions?.length > 0 && (
              <div className="mt-5 pt-4 border-t border-[var(--ivc-line)]">
                <div className="iv-canvas-eyebrow mb-2">Contradictions</div>
                <ul className="space-y-2">
                  {passport.contradictions.map((c, i) => (
                    <li key={i} className="text-[13.5px] text-[var(--ivc-ivory)]">• {c.note || JSON.stringify(c)}</li>
                  ))}
                </ul>
              </div>
            )}
            <p className="mt-6 font-mono text-[10px] tracking-[0.20em] uppercase text-[var(--ivc-gold-2)]">{passport.watermark}</p>
          </div>
        )}
      </div>

      {citationDoc && <CitationModal citation={citationDoc} onClose={() => setCitationDoc(null)} />}
    </Shell>
  );
}

/* ===== helpers ===== */

function Shell({ children }) {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-iv-bg">
      <div className="border-b border-[var(--ivc-line-warm)] bg-[var(--ivc-bg)]">
        <div className="iv-container flex items-center justify-between h-[36px]">
          <span className="font-mono text-[10.5px] tracking-[0.24em] uppercase text-[var(--ivc-gold-2)]">
            Test Workspace · Fictional Data
          </span>
          <div className="flex items-center gap-4">
            <Link to="/app/demo" className="font-mono text-[10.5px] tracking-[0.16em] uppercase text-[var(--ivc-ivory-3)]">
              <RefreshCcw size={12} className="inline mr-1" /> Sample decision
            </Link>
            <button onClick={() => { setTestToken(null); navigate("/"); }} className="font-mono text-[10.5px] tracking-[0.16em] uppercase text-[var(--ivc-ivory-3)]">
              <LogOut size={12} className="inline mr-1" /> Sign out
            </button>
          </div>
        </div>
      </div>
      <div className="iv-container py-8">{children}</div>
    </div>
  );
}
function Loading() {
  return <div className="min-h-[40vh] flex items-center justify-center"><Loader2 className="animate-spin text-iv-gold" size={22} /></div>;
}
function Stat({ label, value }) {
  return (<div><div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[var(--ivc-ivory-3)]">{label}</div><div className="text-[14.5px] text-[var(--ivc-ivory)] mt-1">{value}</div></div>);
}
function PassportCol({ title, items }) {
  if (!items || !items.length) return null;
  return (<div className="mt-5 pt-4 border-t border-[var(--ivc-line)]"><div className="iv-canvas-eyebrow mb-2">{title}</div>{items.map((s, i) => (<p key={i} className="text-[14px] text-[var(--ivc-ivory)]">{s}</p>))}</div>);
}
function PassportCategory({ title, items, openCitation }) {
  if (!items || !items.length) return null;
  return (
    <div className="mt-5 pt-4 border-t border-[var(--ivc-line)]" data-testid={`passport-${title.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="iv-canvas-eyebrow mb-3">{title}</div>
      <ul className="space-y-3">
        {items.map((c) => (
          <li key={c.id} className="border-l border-[var(--ivc-line-warm)] pl-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <p className="text-[14px] text-[var(--ivc-ivory)] flex-1 min-w-0">{c.label}</p>
              {c.evidence_strength && (
                <span className={`iv-strength-badge iv-strength-badge--${c.evidence_strength.tier}`}
                      title={c.evidence_strength.rationale?.join(" · ") || ""}>
                  {c.evidence_strength.tier} · {c.evidence_strength.score}
                </span>
              )}
            </div>
            {c.resolution && c.resolution.status && c.resolution.status !== "open" && (
              <p className="mt-1 font-mono text-[10.5px] tracking-[0.14em] uppercase text-[var(--ivc-gold-2)]">
                Resolution · {c.resolution.status}{c.resolution.note ? ` — ${c.resolution.note}` : ""}
              </p>
            )}
            {c.user_annotation && <p className="font-serif-display italic text-[13px] text-[var(--ivc-ivory-2)] mt-1">Reviewer note: {c.user_annotation}</p>}
            <ul className="mt-2 space-y-1">
              {c.citations.map((ct, i) => (
                <li key={i}>
                  <button onClick={() => openCitation(ct.document_id)} className="font-mono text-[10.5px] tracking-[0.10em] text-[var(--ivc-gold-2)] hover:underline">
                    {ct.document_name} · {ct.location}
                  </button>
                  {ct.excerpt && <p className="font-serif-display italic text-[12.5px] text-[var(--ivc-ivory-2)] mt-1">&ldquo;{ct.excerpt}&rdquo;</p>}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ContradictionSide({ side, label, onOpen }) {
  if (!side) return (
    <div className="border-l border-[var(--ivc-line-warm)] pl-4 opacity-50">
      <div className="font-mono text-[10.5px] tracking-[0.16em] uppercase text-[var(--ivc-ivory-3)]">{label}</div>
      <p className="text-[13px] text-[var(--ivc-ivory-3)] italic mt-1">No citation on this side.</p>
    </div>
  );
  return (
    <div className="border-l border-[var(--ivc-line-warm)] pl-4">
      <div className="font-mono text-[10.5px] tracking-[0.16em] uppercase text-[var(--ivc-ivory-3)]">{label}</div>
      <button onClick={() => onOpen(side.document_id)}
              className="mt-1 text-left font-mono text-[11px] tracking-[0.06em] text-[var(--ivc-gold-2)] hover:underline">
        {side.document_name} · {side.location}
      </button>
      {side.excerpt && (
        <p className="font-serif-display italic text-[13px] leading-relaxed text-[var(--ivc-ivory-2)] mt-2">
          &ldquo;{side.excerpt}&rdquo;
        </p>
      )}
    </div>
  );
}

function TransitionGapMap({ gaps }) {
  if (!gaps || !gaps.length) return null;
  const byCat = {};
  gaps.forEach((g) => { (byCat[g.category] = byCat[g.category] || []).push(g); });
  const CATEGORY_ORDER = ["regulatory", "market", "operational", "technology", "financial", "impact"];
  const cats = CATEGORY_ORDER.filter((c) => byCat[c]?.length);
  return (
    <div className="mt-5 pt-4 border-t border-[var(--ivc-line)]" data-testid="transition-gap-map">
      <div className="iv-canvas-eyebrow mb-2">Transition Credibility Gap Map</div>
      <p className="text-[13px] text-[var(--ivc-ivory-2)] max-w-3xl mb-4">
        Assumptions the thesis depends on that the uploaded documents do not yet ground.
        Each gap names the evidence type needed to close it.
      </p>
      <div className="grid md:grid-cols-2 gap-4">
        {cats.map((cat) => (
          <div key={cat} className="border border-[var(--ivc-line-strong)] rounded-[3px] p-4"
               data-testid={`gap-category-${cat}`}>
            <div className="font-mono text-[10.5px] tracking-[0.20em] uppercase text-[var(--ivc-gold-2)] mb-3">{cat}</div>
            <ul className="space-y-3">
              {byCat[cat].map((g) => (
                <li key={g.id} className="grid grid-cols-[auto_1fr] gap-3" data-testid={`gap-${g.id}`}>
                  <span className={`iv-gap-sev iv-gap-sev--${(g.severity || "Medium").toLowerCase()} mt-1.5`}
                        aria-label={`Severity ${g.severity}`} />
                  <div>
                    <p className="text-[13.5px] text-[var(--ivc-ivory)] leading-snug">{g.assumption}</p>
                    {g.why_it_matters && (
                      <p className="font-serif-display italic text-[12.5px] text-[var(--ivc-ivory-2)] mt-1">{g.why_it_matters}</p>
                    )}
                    {g.needed_evidence?.length > 0 && (
                      <p className="mt-1 font-mono text-[10.5px] tracking-[0.02em] text-[var(--ivc-ivory-3)]">
                        Needs: {g.needed_evidence.join(", ")}
                      </p>
                    )}
                    <p className="mt-1 font-mono text-[10px] tracking-[0.14em] uppercase text-[var(--ivc-ivory-3)]">
                      Severity · {g.severity}{g.jurisdiction ? ` · ${g.jurisdiction}` : ""}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
function CitationModal({ citation, onClose }) {
  const { doc, chunks } = citation;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[rgba(11,13,12,0.72)] backdrop-blur-sm" data-testid="citation-modal">
      <div className="iv-canvas rounded-[3px] max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--ivc-line-strong)]">
          <div>
            <div className="font-mono text-[10.5px] tracking-[0.20em] uppercase text-[var(--ivc-gold-2)]">Source · {doc.kind}</div>
            <div className="text-[15px] text-[var(--ivc-ivory)] mt-1">{doc.name}</div>
          </div>
          <button onClick={onClose} className="iv-btn iv-btn--ghost">Close</button>
        </div>
        <div className="overflow-y-auto p-6 space-y-4">
          {chunks.map((c, i) => (
            <div key={i} className="border-l border-[var(--ivc-line-warm)] pl-4">
              <div className="font-mono text-[10.5px] tracking-[0.14em] uppercase text-[var(--ivc-gold-2)] mb-1">{c.location}</div>
              <p className="text-[13.5px] text-[var(--ivc-ivory-2)] leading-relaxed whitespace-pre-wrap">{c.text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


/* ------------------------------------------------------------------------ */
/* Release Gate + Passport Version panel                                     */
/*                                                                           */
/* Renders three things:                                                     */
/*   1. Release readiness — blocker list from GET /passport/release-check.   */
/*   2. Release button — creates an immutable version snapshot on a clean    */
/*      release (POST /passport/versions), blocked with HTTP 409 by the      */
/*      server if any high-severity blocker is present.                      */
/*   3. Version history — list all past releases, each with a "Diff vs      */
/*      previous" affordance that fetches a structured claim diff.           */
/* ------------------------------------------------------------------------ */

const BLOCKER_COPY = {
  unsupported_conclusions: {
    title: "Unsupported conclusions",
    detail: (
      "Model-derived items must either cite a source or carry the transparent "
      + "AI-generated label prefix. Specialist-review items need a citation or a "
      + "plain-language annotation before the Passport can be released."
    ),
  },
  broken_evidence_links: {
    title: "Broken evidence links",
    detail: (
      "One or more citations reference a document that isn't attached to this "
      + "opportunity, or lack an excerpt. Reattach the source or remove the claim."
    ),
  },
  unresolved_material_contradictions: {
    title: "Unresolved material contradictions",
    detail: (
      "Contradictions marked High/Material must be recorded as resolved (with a "
      + "workspace note) before the Passport can be released."
    ),
  },
};

function ReleaseGatePanel({ releaseCheck, versions, diff, releasing, onRelease, onDiff, onCloseDiff }) {
  const [note, setNote] = React.useState("");
  const ready = !!releaseCheck?.ready;
  const blockers = releaseCheck?.blockers || [];
  return (
    <div className="iv-canvas rounded-[3px] px-6 md:px-10 py-6" data-testid="release-gate-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="iv-canvas-eyebrow">Release readiness</div>
          <p className="text-[13px] text-[var(--ivc-ivory-2)] mt-1 max-w-2xl">
            Every release is captured as a sealed, tamper-evident Passport version.
            The gate below blocks release when any high-severity integrity issue is present.
          </p>
        </div>
        <span
          className={
            "font-mono text-[10.5px] tracking-[0.14em] uppercase px-3 py-1 border "
            + (ready
              ? "border-[var(--ivc-forest)] text-[var(--ivc-forest)]"
              : "border-[var(--ivc-danger,#B4462B)] text-[var(--ivc-danger,#B4462B)]")
          }
          data-testid="release-gate-status"
        >
          {ready ? "Ready to release" : `${blockers.length} blocker${blockers.length === 1 ? "" : "s"}`}
        </span>
      </div>

      {!ready && blockers.length > 0 && (
        <ul className="space-y-3 mb-5" data-testid="release-gate-blockers">
          {blockers.map((b) => {
            const copy = BLOCKER_COPY[b.code] || { title: b.code, detail: b.message };
            return (
              <li key={b.code}
                  data-testid={`release-gate-blocker-${b.code}`}
                  className="border-l-2 border-[var(--ivc-danger,#B4462B)] pl-4 py-1">
                <div className="font-mono text-[10.5px] tracking-[0.14em] uppercase text-[var(--ivc-danger,#B4462B)]">
                  {copy.title} · {b.count}
                </div>
                <p className="text-[13px] text-[var(--ivc-ivory-2)] mt-1 leading-relaxed">
                  {copy.detail}
                </p>
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <input
          type="text"
          placeholder="Release note (optional, max 600 chars)"
          maxLength={600}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="flex-1 min-w-[240px] bg-transparent border-b border-[var(--ivc-line-strong)] py-1 text-[12.5px] text-[var(--ivc-ivory)]"
          data-testid="release-gate-note-input"
        />
        <button
          type="button"
          disabled={!ready || releasing}
          onClick={() => onRelease(note)}
          className="iv-btn iv-btn--primary"
          data-testid="release-gate-submit"
        >
          {releasing ? "Releasing…" : "Release new Passport version"}
        </button>
      </div>

      {versions.length > 0 && (
        <div className="mt-8 pt-6 border-t border-[var(--ivc-line-strong)]" data-testid="passport-versions">
          <div className="iv-canvas-eyebrow mb-3">Passport versions</div>
          <ul className="space-y-2">
            {versions.map((v, i) => {
              const prev = versions[i - 1];
              return (
                <li
                  key={v.id}
                  data-testid={`passport-version-${v.version_num}`}
                  className="grid grid-cols-[auto_1fr_auto] items-center gap-3 border-b border-[var(--ivc-line)] py-2"
                >
                  <span className="font-mono text-[11px] tracking-[0.14em] uppercase text-[var(--ivc-gold-2)]">
                    v{v.version_num}
                  </span>
                  <div className="min-w-0">
                    <div className="text-[13px] text-[var(--ivc-ivory)] truncate">
                      {v.note || <em className="text-[var(--ivc-ivory-3)]">No release note.</em>}
                    </div>
                    <div className="font-mono text-[10.5px] tracking-[0.10em] text-[var(--ivc-ivory-3)] mt-0.5">
                      {new Date(v.released_at).toISOString().slice(0, 19).replace("T", " ")}
                      {" · "}
                      sig {String(v.signature).slice(0, 12)}…
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {prev && (
                      <button
                        type="button"
                        onClick={() => onDiff(v.id, prev.id)}
                        className="iv-btn iv-btn--ghost"
                        data-testid={`passport-version-diff-${v.version_num}`}
                      >
                        Diff vs v{prev.version_num}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {diff && (
        <VersionDiffView diff={diff} onClose={onCloseDiff} />
      )}
    </div>
  );
}

function VersionDiffView({ diff, onClose }) {
  const from = diff.from_version;
  const to = diff.to_version;
  return (
    <div className="mt-6 border border-[var(--ivc-line-strong)] rounded-[3px] p-4" data-testid="version-diff-view">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div className="font-mono text-[10.5px] tracking-[0.16em] uppercase text-[var(--ivc-gold-2)]">
          Diff · v{from?.version_num ?? "0"} → v{to.version_num}
        </div>
        <button type="button" onClick={onClose} className="iv-btn iv-btn--ghost" data-testid="version-diff-close">
          Close
        </button>
      </div>
      {Object.entries(diff.diff.buckets).map(([bucket, delta]) => {
        const total = (delta.added?.length || 0) + (delta.removed?.length || 0) + (delta.modified?.length || 0);
        if (total === 0) return null;
        return (
          <div key={bucket} className="mb-4">
            <div className="font-mono text-[10.5px] tracking-[0.14em] uppercase text-[var(--ivc-ivory-3)] mb-1">
              {bucket.replace(/_/g, " ")} · {delta.added.length} added · {delta.removed.length} removed · {delta.modified.length} modified
            </div>
            {delta.modified.length > 0 && (
              <ul className="space-y-1 mt-1">
                {delta.modified.map((m) => (
                  <li key={m.id} className="text-[12.5px] text-[var(--ivc-ivory-2)] leading-snug">
                    <span className="font-mono text-[10px] tracking-[0.12em] uppercase text-[var(--ivc-gold-2)] mr-2">
                      {Object.keys(m.changes).join(", ")}
                    </span>
                    {m.label || m.id}
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
      <div className="font-mono text-[10.5px] tracking-[0.10em] uppercase text-[var(--ivc-ivory-3)]">
        Signature changed · {diff.diff.signature_before?.slice(0, 12) || "—"} → {diff.diff.signature_after?.slice(0, 12)}
      </div>
    </div>
  );
}
